/**
 * Minimal Help & Tips store (singleton). Fetches the authenticated user's help prefs once,
 * caches them, and exposes merge-safe targeted updates. Fail-soft: a failed request never
 * blocks the page and never causes repeated tips in the same visit.
 */
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULT_PREFS = { auto_tips: true, orientation: {}, ksa_tip: null };

let state = {
  loaded: false,
  loading: false,
  error: false,
  prefs: { ...DEFAULT_PREFS },
  content: null,
  role: null,
  account: null,          // current authenticated account key (isolation)
  reqToken: 0,            // invalidates in-flight responses from a previous account
  orientationShownSession: false, // auto-orientation already surfaced this session
  helpOpen: false,
  restartNonce: 0, // bump to request a manual orientation replay
};
const listeners = new Set();

function emit() { listeners.forEach((fn) => fn(state)); }
function set(patch) { state = { ...state, ...patch }; emit(); }

export function subscribe(fn) { listeners.add(fn); return () => listeners.delete(fn); }
export function getHelpState() { return state; }

/** Clear all in-memory help state (logout / account change). Invalidates in-flight requests. */
export function resetHelp() {
  set({
    loaded: false, loading: false, error: false,
    prefs: { ...DEFAULT_PREFS }, content: null, role: null,
    account: null, reqToken: state.reqToken + 1,
    orientationShownSession: false, helpOpen: false,
  });
}

export function markOrientationShownSession() { set({ orientationShownSession: true }); }

export async function loadHelp(role, account) {
  // Account change → wipe stale in-memory state and reload for the new account.
  if (account !== state.account) {
    set({
      loaded: false, loading: false, prefs: { ...DEFAULT_PREFS }, content: null,
      account, role: role || null, orientationShownSession: false, helpOpen: false,
      reqToken: state.reqToken + 1,
    });
  } else if (state.loaded || state.loading) {
    if (role && !state.role) set({ role });
    return;
  }
  const token = state.reqToken + 1;
  set({ loading: true, role: role || state.role, reqToken: token });
  try {
    const [p, c] = await Promise.all([
      axios.get(`${API}/help/prefs`, { withCredentials: true }),
      axios.get(`${API}/help/content`, { params: { role: role || '' }, withCredentials: true }),
    ]);
    // Ignore responses that belong to a superseded account/request.
    if (token !== state.reqToken || account !== state.account) return;
    set({
      loaded: true, loading: false, error: false,
      prefs: { ...DEFAULT_PREFS, ...(p.data || {}) },
      content: c.data || null,
      role: (c.data && c.data.role) || role || state.role,
    });
  } catch {
    if (token !== state.reqToken || account !== state.account) return;
    // Fail-soft: mark loaded with safe defaults so the page is never blocked and tips don't loop.
    set({ loaded: true, loading: false, error: true, prefs: { ...DEFAULT_PREFS } });
  }
}

async function patch(body, optimistic) {
  if (optimistic) set({ prefs: { ...state.prefs, ...optimistic } });
  try {
    const res = await axios.patch(`${API}/help/prefs`, body, { withCredentials: true });
    if (res.data) set({ prefs: { ...DEFAULT_PREFS, ...res.data } });
  } catch { /* fail-soft: keep optimistic local value */ }
}

export function orientationDone(role) {
  const s = state.prefs.orientation?.[role];
  return s === 'completed' || s === 'dismissed';
}
export function ksaTipDone() {
  return state.prefs.ksa_tip === 'completed' || state.prefs.ksa_tip === 'dismissed';
}
export function autoTipsOn() { return state.prefs.auto_tips !== false; }

export function completeOrientation(role) {
  return patch({ orientation_role: role, orientation_status: 'completed' },
    { orientation: { ...state.prefs.orientation, [role]: 'completed' } });
}
export function dismissOrientation(role) {
  return patch({ orientation_role: role, orientation_status: 'dismissed' },
    { orientation: { ...state.prefs.orientation, [role]: 'dismissed' } });
}
export function completeKsaTip() { return patch({ ksa_tip: 'completed' }, { ksa_tip: 'completed' }); }
export function dismissKsaTip() { return patch({ ksa_tip: 'dismissed' }, { ksa_tip: 'dismissed' }); }
export function setAutoTips(on) { return patch({ auto_tips: !!on }, { auto_tips: !!on }); }

export function openHelp() { set({ helpOpen: true }); }
export function closeHelp() { set({ helpOpen: false }); }
export function requestRestartOrientation() { set({ helpOpen: false, restartNonce: state.restartNonce + 1 }); }

/**
 * "Restart all guidance" (deliberate): re-enable auto tips and make the welcome AND all
 * contextual tips for the current role eligible again, then replay orientation immediately.
 * Resets only the acting role's state (orientation[role] + ksa_tip); other roles / prefs untouched.
 */
export function restartAllGuidance(role) {
  const orientation = { ...(state.prefs.orientation || {}) };
  if (role) delete orientation[role];
  set({
    prefs: { ...state.prefs, auto_tips: true, orientation, ksa_tip: null },
    orientationShownSession: false,
    helpOpen: false,
    restartNonce: state.restartNonce + 1,
  });
  // Persist (fail-soft): server clears this role's orientation + ksa_tip and turns auto tips on.
  patch({ reset_role: role });
}
